import { Translation } from "@/Shared/Logic/Redux/Slices";

export const DEFAULT_LANG = 'fr';
const EXPIRATION_DURATION = 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'amat-translation';

/**
 * I18nHelper class – lightweight i18n handler for managing translation keys and current language state.
 * 
 * Author: Samano CASTRE  
 * Date: August 2, 2025
 * 
 * Responsibilities:
 * - Stores and updates translation content provided by the Redux state
 * - Provides a method to fetch translated strings by key
 * - Acts as a global singleton to avoid context or redundant hooks
 * 
 * Behavior:
 * - Falls back to the original key if no translation is found
 * - Maintains current language code for potential future use (e.g., locale switcher)
 */
class I18nHelper {
    private content?: Record<string, string>;
    private lang?: string;

    /**
     * Updates the i18n content and language.
     * Should be called on application startup or when the language changes.
     */
    update = (i18n: Translation) => {
        this.content = i18n.content;
        this.lang = i18n.lang;
    };

    /**
     * Retrieves the translated value for a given key.
     * If no match is found, returns the key itself.
     * 
     * @param key The translation key (e.g. "app.title")
     * @returns Translated string or the key itself
     */
    __ = (key: string): string =>
        this.content?.[key] ?? key;

    /**
     * Returns the current language code (e.g. "en", "fr")
     */
    getLang = () => this.lang;

    updateI18n = (translationKeyPair :  Record<string, string>, isOnline : boolean, lang : string) => {
        
        const translationFromBack = {
            date: new Date().getTime(),
            status: isOnline ? 'online' : 'offline',
            content: translationKeyPair,
            lang
        } as Translation;

        localStorage.setItem(STORAGE_KEY, JSON.stringify(translationFromBack));
        i18n.update(translationFromBack);
        return translationFromBack;
    }

    isExpired = (i18Cache: Translation) => {
        return (i18Cache.date + EXPIRATION_DURATION) < new Date().getTime();
    }

    isCacheCoherentWithSession = (i18Cache: Translation, isOnline: boolean) => {
        const translationStatus = i18Cache.status;

        if (isOnline && translationStatus === 'online') {
            return true;
        }
        if (!isOnline && translationStatus === 'offline') {
            return true;
        }
        return false;
    }

    getI18nFromCache = (i18nRedux : Translation, isOnline: boolean) => {
        // Look in redux
        if (i18nRedux && !this.isExpired(i18nRedux) && this.isCacheCoherentWithSession(i18nRedux, isOnline)) {
            return i18nRedux;
        }

        // Look in LocalStorage
        const i18nCache = localStorage.getItem(STORAGE_KEY);
        if(!i18nCache) {
            return null;
        }
       
        try {
            let i18nCacheJson = JSON.parse(i18nCache) as Translation;
            if (!this.isExpired(i18nCacheJson) && this.isCacheCoherentWithSession(i18nCacheJson, isOnline)) {
                return i18nCacheJson;
            }
        } catch {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return null;
    };
}

// Singleton instance used globally throughout the application
export const i18n = new I18nHelper();

/**
 * Shortcut function to access translations outside the class instance.
 * Common usage: __('app.welcome')
 */
const __ = (key: string) => i18n.__(key);

export default __;
